import { Button } from '@/view/components/ui/button'
import { Input } from '@/view/components/ui/input'
import { Copy } from 'iconsax-react'
import { useToast } from '@/view/components/ui/use-toast'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/view/components/ui/tooltip'
import { Info } from 'lucide-react'
import { useReferral } from '@/app/hooks/useReferral'
import { Spinner } from '@/view/components/ui/spinner'
import { useAuth } from '@/app/hooks/useAuth'
import { useIsMobile } from '@/app/hooks/useIsMobile'
import { formatMoneyValue } from '@/app/utils/formatMoneyValue'
import { configService } from '@/app/services/configService'
import { useQuery } from '@tanstack/react-query'

export const ReferralCard = () => {
  const { data, isLoading, isFetching } = useReferral()
  const { user } = useAuth()
  const isMobile = useIsMobile()

  const { data: config } = useQuery({
    queryKey: ['config'],
    queryFn: () => configService.getConfigsApp(),
  })

  const USERLINK = `${config?.INVITE_URL}${user?._id}`

  const companyName = import.meta.env.VITE_COMPANY

  const { toast } = useToast()

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(USERLINK)
      toast({
        title: 'Link copiado!',
      })
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  return (
    <div className="flex w-full items-center justify-center">
      <div className="w-full rounded-lg bg-white">
        {(isFetching || isLoading) && (
          <div
            className={`flex items-center justify-center ${isMobile ? 'my-2' : 'my-4'}`}
          >
            <Spinner />
          </div>
        )}
        {!isLoading && data && (
          <>
            <div className="flex items-center gap-2">
              <h2 className={`font-semibold ${isMobile ? 'text-sm' : ''}`}>
                Indique
              </h2>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info size={isMobile ? 14 : 16} color="black" />
                  </TooltipTrigger>
                  <TooltipContent className="w-80 rounded-xl bg-black">
                    <p className="text-white">
                      Indique novos usuários para baixar o app da {companyName}{' '}
                      e se cadastrar. Quando o seu indicado efetuar a primeira
                      venda pelo APP você recebe uma bonificação no valor de{' '}
                      <b>{formatMoneyValue(data?.bonusPerReferral)}</b>.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className={`flex w-full gap-4 ${isMobile ? 'mt-1' : 'mt-2'}`}>
              <div
                className={`w-full rounded-lg bg-success ${isMobile ? 'p-2' : 'p-4'}`}
              >
                <div className="text-white">
                  <div className={`mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    Bonus recebidos:
                  </div>
                  <div
                    className={`font-bold ${isMobile ? 'text-lg' : 'text-2xl'}`}
                  >
                    {formatMoneyValue(data?.bonusTotal || 0)}
                  </div>
                </div>
              </div>

              <div
                className={`w-full rounded-lg border bg-white ${isMobile ? 'p-2' : 'p-4'}`}
              >
                <div className="text-gray-700">
                  <div className={`${isMobile ? 'text-xs' : 'text-sm'}`}>
                    Usuários indicados
                  </div>
                  <div
                    className={`flex gap-2 font-semibold ${isMobile ? 'text-base' : 'text-xl'}`}
                  >
                    <p className="text-black">{data?.referrals}</p>
                    <p className="text-success">({data?.validReferrals})</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div
          className={`relative w-full rounded-lg border bg-gray-50 text-sm ${isMobile ? 'mt-2 p-1 pr-16' : 'mt-4 p-2 pr-20'}`}
        >
          <Input
            type="text"
            value={USERLINK}
            readOnly
            className={`w-full border-none bg-transparent ${isMobile ? 'text-xs' : 'text-sm'}`}
          />
          <Button
            onClick={handleCopyLink}
            className={`absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-1 bg-transparent font-medium text-primary hover:bg-transparent ${isMobile ? 'px-2 py-0.5' : 'px-3 py-1'}`}
          >
            <span className={isMobile ? 'text-xs' : ''}>Copiar</span>
            <Copy size={isMobile ? 12 : 16} />
          </Button>
        </div>
      </div>
    </div>
  )
}
